import { SkeletonComponent } from "@syncfusion/ej2-react-notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useUpdateTask } from "../../data/mutations/taskMutations";
import { useTask } from "../../data/queries/taskQueries";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import useRolePermissionStore from "../../store/rolePermissionStore";
import { Constants } from "../../types/constants";
import { canModifyTask } from "../../utils/helpers";
import { AutoRolloverContent } from "../PreferencesComponents/SystemPreferencesComponents/AutoRolloverContent";
import { CreatedByContainer } from "./CreatedByContainer";
import { TaskInformationContainer } from "./TaskInformationContainer";

/*
 * Important:
 *
 * This tab is **declared outside** the task overlay to avoid unmounting and remounting
 * the component on every state change of the parent component. This can impact performance, and
 * unmounting a component when it is not meant to be unmounted can cause unexpected behaviors and crashes.
 */
export const TaskInformationTab = () => {
  const { t } = useTranslation(["Overlay"], {
    useSuspense: false
  });

  const ids = {
    assignee: "dd_task-overlay_assignee-input",
    stage: "dd_task-overlay_stage-input",
    dueDate: "dd_task-overlay_due-date-input",
    startDate: "dd_task-overlay_start-date-input",
    deleteBtn: "dd_task-overlay_delete-task-button"
  };

  const autoRolloverRef = useRef(null);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");

  const rolePermissions = useRolePermissionStore(
    (state) => state.rolePermissions
  );

  // Parent container already fetches, so we try can use the same data to avoid refetching
  const cachedTask = queryClient.getQueryData(["get-task", taskId]);
  const { data: task } = useTask(taskId, {
    ...Constants.ReactQuery.subscribeOnly
  });
  const { mutate: updateTask } = useUpdateTask();

  const { Events, toggleEvent } = useEvent();
  const { UserId } = useDueDatesAuth();
  const canModifyTasks = canModifyTask(task, UserId, rolePermissions);
  const taskToUse = task ?? cachedTask;

  const onTaskModified = () => {
    let modifiedTask = {
      ...task,
      TemplateId: task?.Template?.Id,
      AutoRollover: autoRolloverRef.current?.getValue(),
      CurrentUserId: UserId,
      Collaborators: null,
      Tags: null,
      AssigneeId: task?.Assignee?.Id
    };

    updateTask(
      { task: modifiedTask },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["get-task", task?.Id]);
          toggleEvent(Events.RefreshTaskGrid);
          toast.success(t("Success"), { position: "bottom-left" });
        },
        onError: () => {
          toast.error(t("ErrorMsg"), { position: "bottom-left" });
        }
      }
    );
  };

  const loader = (
    <div className="w-full d-flex flex-column items-center justify-center gap-4">
      <SkeletonComponent height={20} width={"150px"} shimmerEffect="Fade" />
      <SkeletonComponent
        height={20}
        width={"150px"}
        className="mt-5 mb-2"
        shimmerEffect="Fade"
      />
      <SkeletonComponent
        height={20}
        width={"150px"}
        className="mb-5"
        shimmerEffect="Fade"
      />
      <SkeletonComponent
        height={20}
        width={"70%"}
        className="mt-2 mb-5"
        shimmerEffect="Fade"
      />
      <SkeletonComponent height={100} className="my-2" shimmerEffect="Fade" />
    </div>
  );

  return (
    <div className="task-overlay-content et-of-auto">
      <div className="task-information-content">
        <div className="p-4">
          {taskToUse ? (
            <TaskInformationContainer
              ids={ids}
              task={taskToUse}
              canModifyTasks={canModifyTasks}
            />
          ) : (
            loader
          )}
        </div>

        {task?.TaskType?.AutoRollover && (
          <div className="p-4 et-bt-light-gray-1">
            <div className="my-1">{t("Settings")}</div>
            <AutoRolloverContent
              ref={autoRolloverRef}
              autoRolloverData={taskToUse}
              onChange={onTaskModified}
              disabled={!canModifyTasks}
              show={true}
            />
          </div>
        )}

        <CreatedByContainer
          task={taskToUse}
          rolePermissions={rolePermissions}
          canModifyTasks={canModifyTasks}
        />
      </div>
    </div>
  );
};
